import qs from "qs";
import { useMemo } from "react";
import {
  NavigateOptions,
  To,
  useLocation,
  useMatch,
  useNavigate,
  useParams as useRouteParams,
} from "react-router-dom";

import { AppNavigator } from "@app/shared/lib/app-navigator";
import { isEmpty } from "@app/shared/lib/is-empty";
import { navigateTo } from "@app/shared/lib/navigate-to";

function useParams<T>(): T {
  const routeParams = useRouteParams();

  let searchParams = {};
  const location = useLocation();
  if (!isEmpty(location.search))
    searchParams = qs.parse(location.search.substr(1));

  let hashParams = {};
  if (!isEmpty(location.hash)) {
    hashParams = qs.parse(location.hash.substr(1));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return { ...routeParams, ...searchParams, ...hashParams } as any;
}

export function useRouter<T = Record<string, string>>() {
  const match = useMatch("*");
  const location = useLocation();
  const navigate = useNavigate();
  const navigator = useMemo(() => new AppNavigator(navigate), [navigate]);
  const params = useParams<T>();

  return {
    match,
    location,
    params,
    navigate: (to: To, options?: NavigateOptions) => {
      if (isEmpty(to)) {
        navigate("/", options);

        return;
      }

      const url = to.toString();
      if (url.startsWith("http")) {
        navigateTo(url);

        return;
      }

      navigate(to, options);
    },
    navigator,
    history: {
      push: (url: string) => navigate(url),
      replace: (url: string) => navigate(url, { replace: true }),
    },
  };
}
